import * as yup from 'yup';

/** Wire value from `/atlas/gl-deliveries` — 'atlas' is an internal project
 *  name and is never surfaced to operators (see MaggieTransactionQueries). */
type AtlasGlDeliverySource = 'carta' | 'atlas';

/** One row per file in an Atlas GL delivery (plus a placeholder row with
 *  `fileIndex: null` for a delivery with no files), for the admin Source
 *  Files grid (`AtlasSourceFiles.tsx`). Only `sourceObject`, `fileName`,
 *  `deliveryId`, `createdAt` and `status` are grid columns — the rest back
 *  row logic (downloads, the "No source file" cell). */
export function atlasGlDeliveryFileFields() {
  return {
    sourceObject: yup.string().required().label('Source Object'),
    fileName: yup.string().required().label('File'),
    // An identifier, not a quantity: no thousands separator.
    deliveryId: yup.number().required().label('Delivery ID').customMeta({ formatter: 'string' }),
    createdAt: yup.date().required().label('Created').customMeta({
      formatter: 'dateTimeShort',
    }),
    status: yup.string().required().label('Status'),
    fileFormat: yup.string().required(),
    // Two formats coexist: Carta uploads emit MB, Atlas uploads emit a row
    // count, since Atlas storage doesn't track byte size.
    fileSize: yup.string().required(),
    createdBy: yup.string().required(),
    // Atlas-pipeline rows have no coverageStart until an operator declares
    // one or rows extract a gl_date.
    startDate: yup.date().nullable().defined(),
    source: yup.string().oneOf<AtlasGlDeliverySource>(['carta', 'atlas']).optional(),
    // Null for the placeholder row of a delivery with no files (e.g. QuickBooks).
    fileIndex: yup.number().nullable().defined(),
    hasSourceFile: yup.boolean().required(),
  };
}

export function atlasGlDeliveryFileSchema() {
  return yup.object().shape(atlasGlDeliveryFileFields());
}

export const ATLAS_GL_DELIVERY_FILE_GRID_FIELDS = [
  'sourceObject',
  'fileName',
  'deliveryId',
  'createdAt',
  'status',
] as const;

export type AtlasGlDeliveryFile = yup.InferType<ReturnType<typeof atlasGlDeliveryFileSchema>>;
